import * as Sentry from "@sentry/react";
import { debounce } from "lodash-es";
import { useEffect, useMemo } from "react";
import { FieldErrors, UseFormWatch } from "react-hook-form";

export type TrackingFeature =
  | "login-username-password"
  | "login-phone-otp"
  | "register"
  | "forgot-password"
  | "change-password"
  | "legal-name-update"
  | "bank-account-update"
  | "add-bank-dialog"
  | "add-e-wallet"
  | "add-funding-profile"
  | "deposit-step-topup"
  | "withdraw-form";

type Options = {
  debounceTime?: number;
  feature?: TrackingFeature;
  watch?: UseFormWatch<any>;
};

const SENSITIVE_FIELDS = [
  "password",
  "pin",
  "pinCode",
  "pinCodeConfirm",
  "pinCodeNew",
  "pinCodeConfirmNew",
  "newPassword",
  "confirmNewPassword",
  "secret",
  "sms",
  "code",
];

function createCaptureFormValidationErrors(
  debounceTime: number = 30000,
  feature?: TrackingFeature,
  watch?: UseFormWatch<any>,
) {
  return debounce(
    (errors: FieldErrors<any>) => {
      const currentValues = watch?.() ?? {};
      const errorData = Object.entries(errors).reduce(
        (acc, [key, error]) => {
          acc[key] = {
            message: (error as any)?.message ?? "Unknown error",
            ...(watch && {
              value: SENSITIVE_FIELDS.includes(key) ? "[REDACTED]" : (currentValues as any)?.[key],
            }),
          };
          return acc;
        },
        {} as Record<string, { message: string; value?: any }>,
      );
      Sentry.captureMessage("Form validation failed", {
        level: "warning",
        extra: errorData,
        tags: { feature },
      });
    },
    debounceTime,
    {
      leading: false,
      trailing: true,
    },
  );
}

function useCaptureFormValidationErrors(
  errors: FieldErrors<any>,
  { debounceTime = 30000, feature, watch }: Options = {},
) {
  const capture = useMemo(
    () => createCaptureFormValidationErrors(debounceTime, feature, watch),
    [debounceTime, feature, watch],
  );

  useEffect(() => {
    // Skip capture if all error fields are falsy
    const hasErrors =
      Object.keys(errors).length > 0 && Object.values(errors).some((error) => error !== null && error !== undefined);

    if (hasErrors) {
      capture(errors);
    }
  }, [errors, capture]);
}

export default useCaptureFormValidationErrors;
